import { isDesktopShell } from "../../common/hooks/useIsDesktopView";
import { gptBackground } from "../../components/gpt/layout";
import { GptConversationBreadcrumb } from "../../components/gpt/GptConversationBreadcrumb";
import { useChat } from "@ai-sdk/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import { useChatScrollAnchor } from "../../domains/gpt/hooks/useChatScrollAnchor";
import { useChatKeyboardScroll } from "../../domains/gpt/hooks/useChatKeyboardScroll";
import { displayToast } from "../../components/app/AppToast";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import Loader from "../../components/Loader";
import { GptConversationHeader } from "../../components/gpt/GptConversationHeader";
import { Button } from "../../components/ui/Button";
import Error404Page from "../static/Error404Page";
import LogoHeader from "../../components/app/headers/LogoHeader";
import Navbar from "../../components/app/navbars/Navbar";
import { useHasDesktopSidebar } from "../../components/app/layout/AppLayout";
import ErrorBlock from "../../components/error/ErrorBlock";
import { Logo } from "../../components/ui/Logo";
import { memorizePostLoginRedirectPath } from "../../domains/app/utils/postLoginRedirect";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import {
  apiClient,
  clearApiQuery,
  refetchApiQuery,
  useApiQuery,
} from "../../services/api";
import {
  chatGroundingTitle,
  usePaperGrounding,
} from "../../domains/gpt/hooks/usePaperGrounding";
import {
  assistantUIMessages,
  useGptChat,
  toUIMessages,
  uiMessageQuestionCount,
  type GeneralChatUIMessage,
} from "../../domains/gpt/hooks/useGptChat";
import { useNewConversationStreamRecovery } from "../../domains/gpt/hooks/useGptStreamRecovery";
import { GptConversation } from "../../components/gpt/chat/GptConversation";
import {
  type GptSearchDoc,
  type GptSearchPubmedDoc,
} from "../../components/gpt/chat/GptSearchTurn";
import { EvidenceCitationSheet } from "../../components/gpt/evidence/EvidenceCitationSheet";
import type {
  EvidenceGraphView,
  EvidenceSourceView,
} from "../../domains/gpt/hooks/useEvidenceGptChat";
import { useCopyAnswer } from "../../domains/gpt/hooks/useCopyAnswer";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { goBackInApp } from "../../domains/app/utils/backNavigation";
import { useScrollTriggeredSignupInvite } from "../../domains/auth/hooks/useScrollTriggeredSignupInvite";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { useFirebaseUser } from "../../domains/user/hooks/useFirebaseUser";
import {
  gaEventGptSharedConversationOpened,
  gaEventGptSharedConversationResumeClicked,
} from "../../tools/analytics/domains/gpt.analytics";
import { useKeyboardManaged } from "../../hooks/use-keyboard-managed";
import { useConversationEngine } from "../../domains/assistant/useConversationEngine";
import type { Redaction } from "../../domains/assistant/redaction";
import { useQuestionRedaction } from "../../domains/assistant/useQuestionRedaction";
import { useSingleFlightAction } from "../../hooks/useSingleFlightAction";
import { recordSharedContentLanding } from "../../tools/analytics/shareAttribution";

// Public view of a SHARED JuisciGPT conversation (route /gpt/shared/:token).
// An anonymous visitor reads the transcript (the LoginSheet surfaces on scroll
// or on the resume CTA); a logged-in visitor can "Resume the discussion": the
// composer appears and the first question FORKS the shared transcript into a
// new conversation they own (server-side, lazily — no empty forks), then the
// page hands over to the regular conversation page. The owner-side page is
// GPTConversationPage; this one deliberately omits the access guard.
export default function GPTSharedConversationPage({
  token,
}: {
  token: string;
}): React.ReactElement {
  useKeyboardManaged();
  const conversationQuery = useApiQuery(
    "/webapp/public/conversations/shared/:token",
    { param: { token } }
  );

  if (conversationQuery.state === "pending") {
    return (
      <ScrollableLayout
        containerStyle={{ background: gptBackground() }}
        background={gptBackground()}
        headerClassName="bg-white/90 backdrop-blur-md"
        header={
          <GptConversationHeader
            title={null}
            onBack={null}
            visualAbstracts={null}
            newConversationTo={null}
            actions={null}
          />
        }
      >
        <Loader expanded />
      </ScrollableLayout>
    );
  }

  // Unknown / unshared token → the owner stopped sharing (or it was never public).
  if (conversationQuery.state === "error")
    return conversationQuery.status === 404 ? (
      <NoLongerSharedPage />
    ) : (
      <Error404Page />
    );

  const conversation = conversationQuery.body;
  if (conversation.engine === "assistant")
    return (
      <AssistantSharedConversation
        key={conversation.id}
        id={conversation.id}
        title={conversation.title}
        token={token}
        initialMessages={assistantUIMessages(conversation.messages)}
      />
    );
  const initialMessages = toUIMessages(conversation.messages);

  return (
    <SharedConversation
      // Re-key on the conversation id so a navigation between shared links
      // re-seeds the transcript cleanly.
      key={conversation.id}
      id={conversation.id}
      title={conversation.title}
      token={token}
      paperArticleId={conversation.paperArticleId}
      initialMessages={initialMessages}
      searchTurns={conversation.searchTurns}
      evidenceAnswers={conversation.evidenceAnswers}
      // A fork does not copy the Evidence graphs yet, so the api refuses to
      // resume a conversation with an Evidence turn (409). Same rule here.
      isResumable={conversation.messages.every(
        (message) =>
          message.role !== "user" || message.resolvedMode !== "evidence"
      )}
    />
  );
}

// The desktop top bar of a shared conversation. A member reads it where their
// own conversations sit, under the JuisciGPT landing. A signed-out reader has
// no sidebar and nothing behind the app's crumbs: the bar carries the logo,
// and the door to log in, as the signed-out JuisciGPT page does.
function SharedConversationLeading({
  title,
  isLoggedIn,
  onBack,
}: {
  title: string | null;
  isLoggedIn: boolean;
  onBack: () => void;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const { gptNewChatPageLink } = usePageLinks(null);
  if (!isDesktopShell()) return null;
  if (!isLoggedIn)
    return (
      <Link to="/" aria-label="Juisci" className="flex items-center">
        <Logo variant="no-tagline" tone="color" className="h-6" />
      </Link>
    );
  return (
    <GptConversationBreadcrumb
      askedFrom={null}
      newChatLink={gptNewChatPageLink()}
      onBackToGpt={onBack}
      current={title === null ? t("JuisciGPT conversation") : title}
    />
  );
}

// Log in, then come back to the shared conversation.
function SharedConversationLogIn({
  isLoggedIn,
}: {
  isLoggedIn: boolean;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const history = useHistory();
  if (!isDesktopShell() || isLoggedIn) return null;
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => {
        memorizePostLoginRedirectPath();
        history.push("/login");
      }}
    >
      {t("Log in")}
    </Button>
  );
}

// The link of a conversation its owner stopped sharing.
function NoLongerSharedPage(): React.ReactElement {
  const { t } = useTranslation();
  const hasDesktopSidebar = useHasDesktopSidebar();
  return (
    <ScrollableLayout
      header={hasDesktopSidebar ? null : <LogoHeader />}
      footer={<Navbar />}
    >
      <div className="flex min-h-full flex-col">
        <ErrorBlock
          title={t("This conversation is no longer shared")}
          message={t("The person who shared it stopped sharing it.")}
          action={
            <Link to="/discovery">
              <Button variant="outline" type="button">
                {t("Return to home page")}
              </Button>
            </Link>
          }
        />
      </div>
    </ScrollableLayout>
  );
}

function SharedConversation({
  id,
  title,
  token,
  paperArticleId,
  initialMessages,
  searchTurns,
  evidenceAnswers,
  isResumable,
}: {
  id: string;
  /** The owner's conversation title; null on one whose first turn went unnamed. */
  title: string | null;
  token: string;
  // Non-null when the shared conversation is a Chat-with-Paper one: its
  // grounding paper, rendered as the CWYOP source card like on the owner side.
  paperArticleId: string | null;
  initialMessages: GeneralChatUIMessage[];
  // Past Search turns from the shared hydrate (docs/gpt-mode-router.md), so a
  // shared link renders Search turns as the same article rows the owner sees.
  searchTurns: ReadonlyArray<{
    turnIndex: number;
    framing: string;
    docs: GptSearchDoc[];
    pubmedDocs: GptSearchPubmedDoc[];
  }>;
  // Past Evidence turns' graphs from the shared hydrate, so a shared link renders
  // them with their claims and cited sources like the owner side.
  evidenceAnswers: ReadonlyArray<{
    turnIndex: number;
    answer: EvidenceGraphView;
  }>;
  /** False when an Evidence turn keeps the visitor from resuming it. */
  isResumable: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const { firebaseUser } = useFirebaseUser();
  const isLoggedIn = firebaseUser !== null;
  const { gptNewChatPageLink, gptConversationPageLink, articleLink } =
    usePageLinks(null);
  // Search turns keyed by turnIndex. Read-only view: the "search deeper"
  // refinement chips are dropped (no composer to run them), but the result rows
  // render exactly like the owner side.
  const searchByTurnIndex = new Map<
    number,
    {
      framing: string;
      docs: GptSearchDoc[];
      pubmedDocs: GptSearchPubmedDoc[];
      refinements: string[];
    }
  >();
  for (const turn of searchTurns)
    searchByTurnIndex.set(turn.turnIndex, {
      framing: turn.framing,
      docs: turn.docs,
      pubmedDocs: turn.pubmedDocs,
      refinements: [],
    });
  const evidenceGraphByTurnIndex = new Map<number, EvidenceGraphView>();
  for (const turn of evidenceAnswers)
    evidenceGraphByTurnIndex.set(turn.turnIndex, turn.answer);
  // The citation sheet an Evidence turn's citation badge opens.
  const [openEvidenceSource, setOpenEvidenceSource] = useState<{
    source: EvidenceSourceView;
    citationNumber: number;
    quotes: readonly string[];
  } | null>(null);
  const { scrollRef, isAtBottom, scrollToBottom, onScroll } =
    useChatScrollAnchor({ initialToBottom: false });
  const { paddingBottom: keyboardPaddingBottom } =
    useChatKeyboardScroll(scrollToBottom);
  // The signup invitation is for anonymous visitors only — an already
  // authenticated reader is never nudged to sign up.
  const invite = useScrollTriggeredSignupInvite(!isLoggedIn);
  const copyAnswer = useCopyAnswer();
  // The paper's own details decide the label ("paper" vs abstract-only) — same
  // derivation as the owner-side page, off the same batch endpoint the sources
  // list already queries for this id (one request, deduped by the query cache).
  const paperQuery = useApiQuery(
    "/webapp/public/articles/batch",
    paperArticleId === null
      ? false
      : { query: { ids: paperArticleId }, durable: true }
  );
  const paper =
    paperQuery.state === "success" ? paperQuery.body.at(0) : undefined;
  // The shared transcript names the grounding the same way the owner's own page
  // does: one table, so a reader who follows the link reads the same word the
  // author saw. Until both the paper and its grounding land, the label is the
  // FEATURE's name, as on the owner's page. The id is known from the start, so
  // the citations are numbered without the paper from the first frame and
  // never shift once it loads.
  const grounding = usePaperGrounding(paper === undefined ? "" : paper.slug);
  const cwyopPaper =
    paperArticleId === null
      ? null
      : {
          articleId: paperArticleId,
          label:
            paper === undefined || grounding.status !== "ready"
              ? t("Chat With Paper")
              : chatGroundingTitle(t, grounding.kind),
        };
  // Resume flow: the CTA swaps for the composer, and the first submitted
  // question forks the shared transcript server-side.
  const [isResuming, setIsResuming] = useState(false);
  // The forked conversation id arrives as a transient data part at stream
  // start; the end-of-stream navigation reads it from this ref (state would be
  // a stale closure inside onTurnFinish).
  const conversationIdRef = useRef<string | null>(null);

  // The fork is written at completion, exactly like a brand-new conversation, so
  // a connection dropped mid-fork waits for it to appear in the visitor's history
  // and then opens it — the fork itself is never lost, the client just stopped
  // listening. Same hook as the new-chat landing.
  const recovery = useNewConversationStreamRecovery({
    getConversationId: () => conversationIdRef.current,
    onTurnRecovered: (recoveredId) => {
      // The fork is now the user's last conversation: the landing greeting
      // must name its subject, not the previous one's.
      clearApiQuery("/webapp/private/conversations/latest-topic", {});
      // No hand-off state: the forked conversation is in the DB, so its page seeds
      // itself from it.
      history.replace(`${gptConversationPageLink(recoveredId)}?new=true`);
    },
    onTurnLost: () => {
      stopLostTurn();
    },
  });

  // The recovery closed its window on a fork that never appeared. The chat may
  // still believe it is streaming — a socket the OS suspended dies in silence —
  // and would hold the composer locked forever; stop it, and let the failure
  // surface (`hasLostTurn`) take over.
  function stopLostTurn(): void {
    void chat.stop();
  }

  const chat = useGptChat({
    transport: { kind: "shared-fork", token, language: activeLang },
    initialMessages,
    onTurnAccepted: null,
    onConversationCreated: (forkedId) => {
      conversationIdRef.current = forkedId;
    },
    onTurnFinish: (question, answer) => {
      recovery.onTurnSettled();
      // The fork is now the user's last conversation, and the conversation
      // page's own turn-end never runs for this seeded turn: remove the
      // greeting's subject here, as that page does for its turns. Before the
      // early return: a fork stopped before its id or any answer text arrived
      // is still completed and persisted by the server.
      clearApiQuery("/webapp/private/conversations/latest-topic", {});
      const forkedId = conversationIdRef.current;
      if (forkedId === null || answer === "") return;
      // Hand the streamed content over so the conversation page renders
      // instantly (no flash) while the completion-time DB write settles; the
      // next turns ride the regular continuation route.
      history.replace(`${gptConversationPageLink(forkedId)}?new=true`, {
        question,
        answer,
      });
    },
    // No toast: `GPTChatError` (rendered in the conversation once the recovery
    // window has closed) is the single feedback surface, and it can tell a REFUSAL
    // (out of credits, with when to come back and the push opt-in) from a real
    // failure by asking the server's typed budget state. A toast here could only
    // guess from the error text — which is what the removed
    // `["daily","limit","reach"]` sniffing did, missing the weekly ceiling and
    // mislabelling other errors.
    onFailure: recovery.onFailure,
  });

  useEffectOnce(() => {
    gaEventGptSharedConversationOpened({
      conversationId: id,
      isLoggedIn: firebaseUser !== null,
    });
    // A visitor may sign up next: the conversation credits its owner, whom
    // the api resolves from this id (`shareAttribution.ts`).
    if (firebaseUser === null)
      recordSharedContentLanding({
        kind: "gpt_conversation",
        conversationId: id,
      });
  });

  function handleResume(): void {
    gaEventGptSharedConversationResumeClicked({
      conversationId: id,
      isLoggedIn,
    });
    // Anonymous visitors get the signup sheet — resuming requires an account
    // (the fork becomes a conversation they own).
    if (!isLoggedIn) {
      invite.open();
      return;
    }
    setIsResuming(true);
  }

  function submit(question: string): void {
    if (question === "") return;
    recovery.onTurnSent(question);
    // The shared view has no suggestion chips (follow-ups are null) — a resumed
    // question is always manually typed.
    void chat.sendMessage({ text: question }, { body: { origin: "typed" } });
  }

  const handleBack = useSharedConversationBack(isLoggedIn);

  function handleCopyAnswer(question: string, text: string): void {
    copyAnswer(question, text, () => {
      displayToast(
        isDesktopShell() ? t("Answer copied") : t("Copied"),
        "success"
      );
    });
  }

  return (
    <>
      <ScrollableLayout
        containerStyle={{ background: gptBackground() }}
        background={gptBackground()}
        headerClassName="bg-white/90 backdrop-blur-md"
        header={
          <GptConversationHeader
            title={title}
            onBack={handleBack}
            // Public read-only view: no grounding paper of the reader's own, so
            // no posters to list.
            visualAbstracts={null}
            // An anonymous reader has no chat to start — the resume CTA (which
            // routes through the login sheet) is their entry point instead.
            newConversationTo={isLoggedIn ? gptNewChatPageLink() : null}
            // A reader is not the owner: nothing here to rename or delete.
            actions={null}
          />
        }
        scrollRef={scrollRef}
        onScroll={() => {
          onScroll();
          const el = scrollRef.current;
          if (el !== null) invite.handleScroll(el.scrollTop);
        }}
        style={{ paddingBottom: keyboardPaddingBottom }}
        hideScrollbar
      >
        <GptConversation
          chat={chat}
          leading={
            <SharedConversationLeading
              title={title}
              isLoggedIn={isLoggedIn}
              onBack={handleBack}
            />
          }
          headerActions={<SharedConversationLogIn isLoggedIn={isLoggedIn} />}
          afterTurns={null}
          // Read-only: it does nothing, so it reports nothing.
          composerNotice={null}
          // Read-only public view: no composer band, and the reader is not the
          // conversation's owner — so no proposals and nothing to attach to.
          // The reader may not even be signed in, so no push ask either.
          suggestion={null}
          pushOptIn={null}
          attachments={null}
          canModifyAttachments={false}
          // Read-only surface — nothing to reference either.
          references={null}
          study={null}
          canModifyReferences={false}
          cwyopPaper={cwyopPaper}
          showModePicker={false}
          evidenceGraphByTurnIndex={evidenceGraphByTurnIndex}
          evidenceCheckByRunId={new Map()}
          onOpenEvidenceSource={(source, citationNumber, quotes) => {
            setOpenEvidenceSource({ source, citationNumber, quotes });
          }}
          pendingEvidence={null}
          searchByTurnIndex={searchByTurnIndex}
          pendingSearch={null}
          onOpenSearchArticle={(article) => {
            history.push(articleLink(article));
          }}
          onRefineSearch={() => undefined}
          // Read-only share view: re-asking in General mode would start a turn,
          // which this surface cannot do. Null drops the recovery button, like the
          // refinement chips (fed an empty array above).
          onAskGeneral={null}
          onAnswerCard={null}
          // A shared paper chat renders like its owner-side page: LaTeX
          // resolved (paper answers carry formulas).
          answerMath={paperArticleId !== null}
          actions={{
            onCopy: handleCopyAnswer,
            // Shared surface: share button hidden, no authoring actions.
            onShare: () => undefined,
            showShare: false,
            onRegenerate: null,
            onRetry: null,
            // A shared conversation is read-only, and its reader is not its
            // author — the rubric is judged by whoever asked the question.
            onEvaluate: null,
            evaluatedTurnIndexes: [],
            comingSoon: () => undefined,
          }}
          followUps={null}
          disclaimer={t(
            "AI can make mistakes. Consider checking Source information."
          )}
          background={gptBackground()}
          roomId={null}
          assistantConversationId={null}
          viewStateKey={id}
          onOpenSources={() => undefined}
          // Read-only until the visitor resumes: the CTA holds the composer's
          // band — so the transcript clears it exactly as it clears an input —
          // and the input replaces it in place once resuming, submitting
          // fork-stream turns.
          composer={
            isResuming
              ? {
                  kind: "input",
                  onSubmit: submit,
                  onStop: () => {
                    // Stop waiting as well: the fork still completes server-side
                    // and shows up in the visitor's history, but the composer
                    // comes back now.
                    recovery.onTurnSettled();
                    void chat.stop();
                  },
                  placeholder: t("Keep asking..."),
                }
              : {
                  kind: "cta",
                  content: isResumable ? (
                    <Button
                      variant="primary"
                      className="w-full"
                      onClick={handleResume}
                    >
                      {t("Resume the discussion")}
                    </Button>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <p className="m-0 text-center font-dm-sans text-ui-body text-ds-slate-700">
                        {t(
                          "This conversation contains Evidence answers and cannot be resumed. Ask your question in a new conversation."
                        )}
                      </p>
                      <Button
                        variant="primary"
                        className="w-full"
                        onClick={() => {
                          // A new conversation needs an account, like a resume.
                          if (!isLoggedIn) {
                            invite.open();
                            return;
                          }
                          history.push(gptNewChatPageLink());
                        }}
                      >
                        {t("New conversation")}
                      </Button>
                    </div>
                  ),
                }
          }
          isReconnecting={recovery.isReconnecting}
          hasLostTurn={recovery.isLost}
          // No history drawer on a shared conversation.
          followKeyboard
          scrollRef={scrollRef}
          isAtBottom={isAtBottom}
          onScrollToBottom={scrollToBottom}
          revealAssistantId={null}
        />
      </ScrollableLayout>
      <EvidenceCitationSheet
        source={openEvidenceSource === null ? null : openEvidenceSource.source}
        citationNumber={
          openEvidenceSource === null ? null : openEvidenceSource.citationNumber
        }
        quotes={openEvidenceSource === null ? [] : openEvidenceSource.quotes}
        roomId={null}
        isOpen={openEvidenceSource !== null}
        onClose={() => {
          setOpenEvidenceSource(null);
        }}
      />
      {invite.sheet}
    </>
  );
}

// Back from a shared conversation. Shared links mostly open cold: a link a
// colleague sent, or the website's "See the answer", with no page of ours
// behind them. `history.length` counts the tab, not the app, so it sent the
// reader out of the app or nowhere; `canGoBackInApp` reads the router's own
// entry instead (`docs/shared-content-access/README.md`, "Where the Back button
// goes"). With no page of ours behind it, Back opens the JuisciGPT landing for
// a signed-in reader and `/`, the front door, for a signed-out one.
function useSharedConversationBack(isLoggedIn: boolean): () => void {
  const history = useHistory();
  const { gptNewChatPageLink } = usePageLinks(null);
  return () => {
    goBackInApp(history, isLoggedIn ? gptNewChatPageLink() : "/");
  };
}

// A share of the assistant engine. Resuming forks it into a conversation of the
// reader's, whose page asks the first question.
function AssistantSharedConversation({
  id,
  title,
  token,
  initialMessages,
}: {
  id: string;
  title: string | null;
  token: string;
  initialMessages: GeneralChatUIMessage[];
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const { firebaseUser } = useFirebaseUser();
  const isLoggedIn = firebaseUser !== null;
  const { gptNewChatPageLink, gptConversationPageLink } = usePageLinks(null);
  const { scrollRef, isAtBottom, scrollToBottom, onScroll } =
    useChatScrollAnchor({ initialToBottom: false });
  const { paddingBottom: keyboardPaddingBottom } =
    useChatKeyboardScroll(scrollToBottom);
  const invite = useScrollTriggeredSignupInvite(!isLoggedIn);
  const copyAnswer = useCopyAnswer();
  const engine = useConversationEngine();
  const [isResuming, setIsResuming] = useState(false);
  // Minted once, so a retried fork reuses the conversation it may have created.
  const [forkId] = useState(() => crypto.randomUUID());
  const forking = useSingleFlightAction();
  // One route: every Question forks the conversation.
  const questionRedaction = useQuestionRedaction<null>({
    enabled: true,
    deliver: (redaction) => {
      void submit(redaction);
    },
    failedTextPlace: {
      kind: "transcript",
      // The shared transcript never grows: a Question leaves for the fork's page.
      questionCount: initialMessages.reduce(
        (count, message) => count + uiMessageQuestionCount(message),
        0
      ),
    },
  });
  // Read-only: the question goes to the fork's own page.
  const chat = useChat<GeneralChatUIMessage>({
    id,
    messages: initialMessages,
    transport: {
      sendMessages: () => {
        throw new Error("should not happen: a shared view sends nothing");
      },
      reconnectToStream: () => {
        throw new Error("should not happen: a shared view resumes nothing");
      },
    },
  });

  useEffectOnce(() => {
    gaEventGptSharedConversationOpened({ conversationId: id, isLoggedIn });
    // A visitor may sign up next: the conversation credits its owner, whom
    // the api resolves from this id (`shareAttribution.ts`).
    if (!isLoggedIn)
      recordSharedContentLanding({
        kind: "gpt_conversation",
        conversationId: id,
      });
  });

  function handleResume(): void {
    gaEventGptSharedConversationResumeClicked({
      conversationId: id,
      isLoggedIn,
    });
    if (!isLoggedIn) {
      invite.open();
      return;
    }
    setIsResuming(true);
  }

  async function submit(redaction: Redaction): Promise<void> {
    if (redaction.text === "") return;
    await forking.run(async () => {
      try {
        await apiClient.post("/assistant/shared/:token/fork", {
          param: { token },
          json: { id: forkId },
        });
        // The fork's page seeds its chat from this read, shared turns included.
        await refetchApiQuery("/assistant/conversations/:conversationId", {
          param: { conversationId: forkId },
        });
      } catch {
        displayToast(t("Something went wrong. Please try again."), "error");
        return;
      }
      history.replace(`${gptConversationPageLink(forkId)}?new=true`, {
        pending: {
          question: redaction.text,
          origin: "typed",
          requestId: crypto.randomUUID(),
          notice: redaction.notice,
        },
      });
    });
  }

  const handleBack = useSharedConversationBack(isLoggedIn);

  function handleCopyAnswer(question: string, text: string): void {
    copyAnswer(question, text, () => {
      displayToast(
        isDesktopShell() ? t("Answer copied") : t("Copied"),
        "success"
      );
    });
  }

  return (
    <>
      <ScrollableLayout
        containerStyle={{ background: gptBackground() }}
        background={gptBackground()}
        headerClassName="bg-white/90 backdrop-blur-md"
        header={
          <GptConversationHeader
            title={title}
            onBack={handleBack}
            visualAbstracts={null}
            newConversationTo={isLoggedIn ? gptNewChatPageLink() : null}
            actions={null}
          />
        }
        scrollRef={scrollRef}
        onScroll={() => {
          onScroll();
          const el = scrollRef.current;
          if (el !== null) invite.handleScroll(el.scrollTop);
        }}
        style={{ paddingBottom: keyboardPaddingBottom }}
        hideScrollbar
      >
        <GptConversation
          chat={chat}
          leading={
            <SharedConversationLeading
              title={title}
              isLoggedIn={isLoggedIn}
              onBack={handleBack}
            />
          }
          headerActions={<SharedConversationLogIn isLoggedIn={isLoggedIn} />}
          afterTurns={null}
          composerNotice={null}
          suggestion={null}
          pushOptIn={null}
          attachments={null}
          canModifyAttachments={false}
          references={null}
          study={null}
          canModifyReferences={false}
          cwyopPaper={null}
          showModePicker={false}
          // The assistant engine writes no Evidence or Search turn.
          evidenceGraphByTurnIndex={new Map()}
          evidenceCheckByRunId={new Map()}
          onOpenEvidenceSource={() => {
            throw new Error(
              "should not happen: an Evidence turn on the assistant engine"
            );
          }}
          pendingEvidence={null}
          searchByTurnIndex={new Map()}
          pendingSearch={null}
          onOpenSearchArticle={() => {
            throw new Error(
              "should not happen: a Search turn on the assistant engine"
            );
          }}
          onRefineSearch={() => {
            throw new Error(
              "should not happen: a Search turn on the assistant engine"
            );
          }}
          onAskGeneral={null}
          onAnswerCard={null}
          answerMath={false}
          actions={{
            onCopy: handleCopyAnswer,
            onShare: () => undefined,
            showShare: false,
            onRegenerate: null,
            onRetry: null,
            onEvaluate: null,
            evaluatedTurnIndexes: [],
            comingSoon: () => undefined,
          }}
          followUps={null}
          disclaimer={t(
            "AI can make mistakes. Consider checking Source information."
          )}
          background={gptBackground()}
          roomId={null}
          assistantConversationId={null}
          viewStateKey={id}
          onOpenSources={() => undefined}
          composer={
            isResuming
              ? {
                  kind: "assistant",
                  onSubmit: (question) => {
                    questionRedaction.sendRedacted(question, null);
                  },
                  // Nothing streams here: the fork's page runs the question.
                  onStop: () => undefined,
                  placeholder: t("Keep asking..."),
                  // The fork's page sends the question and shows its Notice.
                  banner: questionRedaction.showInputRule
                    ? {
                        kind: "inputRule",
                        onDismiss: questionRedaction.dismissInputRule,
                      }
                    : null,
                  unsent: questionRedaction.unsent,
                  overlapsMatch: questionRedaction.overlapsMatch,
                }
              : {
                  kind: "cta",
                  // A fork is an assistant conversation, which the legacy lists do not show.
                  content:
                    isLoggedIn && engine === "legacy" ? (
                      <Button
                        variant="primary"
                        className="w-full"
                        onClick={() => {
                          history.push(gptNewChatPageLink());
                        }}
                      >
                        {t("New conversation")}
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        className="w-full"
                        disabled={isLoggedIn && engine === "pending"}
                        onClick={handleResume}
                      >
                        {t("Resume the discussion")}
                      </Button>
                    ),
                }
          }
          isReconnecting={false}
          hasLostTurn={false}
          followKeyboard
          scrollRef={scrollRef}
          isAtBottom={isAtBottom}
          onScrollToBottom={scrollToBottom}
          revealAssistantId={null}
        />
      </ScrollableLayout>
      {invite.sheet}
    </>
  );
}
